import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LocalStorageKeys, removeDataFromLocalStorage } from '@siat-fe/auth';
import { environment } from '@siat-fe/env';
import { CustomValidators, SnackbarService } from '@siat-fe/shared';
import {
  catchError,
  EMPTY,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { LoginRequest, LoginResponse } from '../models/login.model';
import { User } from '../models/userinfo.model';
import { AuthenticationService } from '../services/authentication.service';
import { MenuNavigationService } from '../services/menu-navigation.service';
import {
  GetIsNotificationRedirect,
  SetIsNotificationRedirect,
} from '../services/storage-data.utility';
import { UserProvider } from '../services/user.provider';
import { resolveRedirectTarget } from '../redirect-target';
import { FirebaseStore } from '../store/firebase.store';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'lib-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  /**
   * activation code timeout, in milliseconds.
   */
  private readonly activationCodeTimeoutMs = 120000;

  /**
   * cancels the activation code timeout when emitted.
   */
  private readonly activationCodeTimeoutCancel$ = new Subject<void>();

  /**
   * whether the entra login option should be shown in environments where it is enabled.
   */
  readonly showEntraLogin =
    (environment.production === true ||
      environment.staging === true ||
      environment.test === true) &&
    environment.entra.enabled === true;

  /**
   * whether an activation code is required before login.
   */
  readonly requiresActivationCode =
    environment.production === true || environment.test === true;

  /**
   * whether the codice fiscale/password form is shown (vs. the entra option).
   */
  showCredentialsLogin = signal(!this.showEntraLogin);

  /**
   * whether the activation code has been generated.
   */
  activationCodeGenerated = signal(false);

  /**
   * whether the activation code is being generated.
   */
  isGeneratingActivationCode = signal(false);

  /**
   * whether a login request is in flight.
   */
  isLoggingIn = signal(false);

  /**
   * login form.
   */
  loginForm: FormGroup = new FormGroup({
    codiceFiscale: new FormControl('', [
      Validators.required,
      CustomValidators.invalidCf,
    ]),
    password: new FormControl('', [Validators.required]),
    pin: new FormControl(''),
  });

  /**
   * current login error message.
   */
  loginError = signal('');

  /**
   * injected services.
   */
  activeModal = inject(MatDialogRef, { optional: true });
  router = inject(Router);
  route = inject(ActivatedRoute);
  authService = inject(AuthenticationService);
  menuNavigationService = inject(MenuNavigationService);
  userProvider = inject(UserProvider);
  snackbarService = inject(SnackbarService);
  firebaseStore = inject(FirebaseStore);

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    this.loginForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.loginError() || this.loginForm.hasError('invalidCredentials')) {
        this.loginError.set('');
        this.loginForm.setErrors(null);
      }
    });

    this.loginForm
      .get('codiceFiscale')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => this.resetActivationCodeStep());

    this.loginForm
      .get('password')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => this.resetActivationCodeStep());
  }

  /**
   * @inheritdoc
   */
  ngOnDestroy(): void {
    this.clearActivationCodeTimeout();
  }

  /**
   * dispatches the form submit to activation-code generation or login, depending on the current step.
   */
  onSubmit(): void {
    if (!this.requiresActivationCode) {
      if (this.credentialsInvalid || this.isLoggingIn()) {
        return;
      }
      this.login();
      return;
    }

    if (!this.activationCodeGenerated()) {
      if (this.credentialsInvalid || this.isGeneratingActivationCode()) {
        return;
      }
      this.generateActivationCode();
      return;
    }

    if (this.loginForm.invalid || this.isLoggingIn()) {
      return;
    }

    this.login();
  }

  /**
   * generates the activation code and sends it to the user's registered email.
   */
  generateActivationCode(): void {
    removeDataFromLocalStorage(LocalStorageKeys.TOKEN);
    removeDataFromLocalStorage(LocalStorageKeys.REFRESH_TOKEN);

    const loginRequest = this.buildCredentialsRequest();

    this.isGeneratingActivationCode.set(true);
    this.authService
      .generateActivationCode(loginRequest)
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.isGeneratingActivationCode.set(false);
          this.activationCodeGenerated.set(true);
          this.loginForm.get('pin')?.setValidators([Validators.required]);
          this.loginForm.get('pin')?.updateValueAndValidity();
          this.startActivationCodeTimeout();
        }),
        catchError((error) => {
          this.isGeneratingActivationCode.set(false);
          this.loginError.set(
            error.error?.errorMessage ||
              'Si è verificato un errore durante la generazione del codice di attivazione, riprova.',
          );
          this.loginForm.setErrors({ invalidCredentials: true });
          this.loginForm.markAllAsDirty();
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /**
   * logs the user in, persists the session, and navigates to the target page on success.
   */
  login(): void {
    removeDataFromLocalStorage(LocalStorageKeys.TOKEN);
    removeDataFromLocalStorage(LocalStorageKeys.REFRESH_TOKEN);

    const loginRequest = {
      ...this.buildCredentialsRequest(),
      pin: this.loginForm.get('pin')?.value,
    } as LoginRequest;

    const storedUserCf = this.userProvider.userInfo?.codiceFiscale;
    const isSessionExpired =
      this.route.snapshot.queryParams['sessionExpired'] === 'true';

    this.isLoggingIn.set(true);
    this.authService
      .login(loginRequest)
      .pipe(
        untilDestroyed(this),
        switchMap((credential: LoginResponse) => {
          const isDifferentUser =
            storedUserCf && storedUserCf !== loginRequest.codiceFiscale;

          if (isSessionExpired && isDifferentUser) {
            this.userProvider.clearUserProviderForLogout(true);
          }

          this.userProvider.setCredentials(credential);
          return this.authService.getUserInfo(credential.user_id).pipe(
            tap((user: User) => {
              this.userProvider.setCurrentUser(user);
              this.userProvider.setIsAdmin(user?.administrator || false);
              this.userProvider.setStruttureList(user.strutture);
              if (!this.userProvider.isAdminUser) {
                this.requestPermission();
              }
              this.clearActivationCodeTimeout();
              this.activeModal?.close();
              this.handleLoginNavigation(isSessionExpired && isDifferentUser);
            }),
          );
        }),
        catchError((error) => {
          this.isLoggingIn.set(false);
          this.userProvider.clearUserProviderForLogout(true);
          this.showCredentialsLogin.set(true);
          this.loginError.set(
            error.error?.errorMessage ||
              'Si è verificato un errore di autenticazione, riprova.',
          );
          this.loginForm.setErrors({ invalidCredentials: true });
          this.loginForm.markAllAsDirty();
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /**
   * checks whether the codice fiscale or password fields are invalid.
   * @returns {boolean} true if either field is invalid.
   */
  get credentialsInvalid(): boolean {
    return (
      !!this.loginForm.get('codiceFiscale')?.invalid ||
      !!this.loginForm.get('password')?.invalid
    );
  }

  /**
   * closes this dialog and starts the entra redirect login flow.
   */
  loginWithEntra(): void {
    if (!this.showEntraLogin) {
      return;
    }

    const redirectTo = this.route.snapshot.queryParams['redirectTo'];
    const sessionExpired = this.route.snapshot.queryParams['sessionExpired'];
    this.activeModal?.close();
    this.router.navigate(['/entra-authenticator'], {
      queryParams: {
        redirectTo,
        sessionExpired,
        startLogin: 'true',
      },
      replaceUrl: true,
    });
  }

  /**
   * switches the dialog to the codice fiscale/password form.
   */
  showEmailPasswordLogin(): void {
    this.showCredentialsLogin.set(true);
  }

  /**
   * builds the codice fiscale/password login request from the form.
   * @returns {LoginRequest} the login request.
   */
  private buildCredentialsRequest(): LoginRequest {
    return {
      codiceFiscale: this.loginForm.get('codiceFiscale')?.value,
      password: this.loginForm.get('password')?.value,
    } as LoginRequest;
  }

  /**
   * (re)starts the activation code expiry timeout.
   */
  private startActivationCodeTimeout(): void {
    this.clearActivationCodeTimeout();
    timer(this.activationCodeTimeoutMs)
      .pipe(takeUntil(this.activationCodeTimeoutCancel$), untilDestroyed(this))
      .subscribe(() => this.resetActivationCodeStep());
  }

  /**
   * cancels the pending activation code expiry timeout.
   */
  private clearActivationCodeTimeout(): void {
    this.activationCodeTimeoutCancel$.next();
  }

  /**
   * resets the activation code step back to "not generated" and clears the pin field.
   */
  private resetActivationCodeStep(): void {
    if (!this.activationCodeGenerated()) {
      return;
    }

    this.activationCodeGenerated.set(false);
    this.loginForm.get('pin')?.reset('', { emitEvent: false });
    this.loginForm.get('pin')?.clearValidators();
    this.loginForm.get('pin')?.updateValueAndValidity({ emitEvent: false });
    this.clearActivationCodeTimeout();
  }

  /**
   * navigates to the target page after a successful login, restoring the previous app/struttura context.
   */
  private handleLoginNavigation(isDifferentUserAfterExpiry: boolean): void {
    const redirectTo = this.route.snapshot.queryParams['redirectTo'];
    const isNotificationRoute =
      typeof redirectTo === 'string' &&
      redirectTo.startsWith('/home/notifiche');
    const isNotificationRedirect =
      GetIsNotificationRedirect() || isNotificationRoute;

    if (isDifferentUserAfterExpiry) {
      this.router.navigate(['/apps'], { replaceUrl: true });
      return;
    }

    if (isNotificationRedirect) {
      if (isNotificationRoute) {
        SetIsNotificationRedirect(true);
      }
      this.menuNavigationService.setSelectedApp('home');
      this.router.navigate(['/apps'], { replaceUrl: true });
      return;
    }

    const target = resolveRedirectTarget(redirectTo);
    const url = target ?? '/apps';
    if (target) {
      this.restorePreviousState(target);
    }

    this.router.navigateByUrl(url, { replaceUrl: true });
  }

  /**
   * restores the selected app and struttura context that was active before the redirect.
   */
  private restorePreviousState(redirectTo: string): void {
    const appId = this.getAppId(redirectTo) || '';
    this.menuNavigationService.setSelectedApp(appId);

    const selectedStruttura = this.userProvider.currentStruttura;
    if (selectedStruttura) {
      this.userProvider.setSelectedStruttura(selectedStruttura);
      this.userProvider.setPermissionList(
        selectedStruttura?.permessiVisivi || [],
      );
    }
  }

  /**
   * requests push notification permission and initializes firebase messaging if granted.
   */
  private async requestPermission(): Promise<void> {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        this.firebaseStore.initFirebase();
      }
    } catch {
      return;
    }
  }

  /**
   * extracts the app id (first path segment) from a redirect url.
   * @returns {string | null} the app id, or null if the path has no segments.
   */
  private getAppId(path: string): string | null {
    const firstSlashIndex = path.indexOf('/');

    if (firstSlashIndex === -1 || firstSlashIndex === path.length - 1) {
      return null;
    }

    const secondSlashIndex = path.indexOf('/', firstSlashIndex + 1);

    if (secondSlashIndex === -1) {
      return path.substring(firstSlashIndex + 1);
    }

    return path.substring(firstSlashIndex + 1, secondSlashIndex);
  }
}
